/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventStepActivities — step 2 of 3.
 *
 * The only genuinely complex part of the wizard, so it gets a whole step to
 * itself with nothing else competing for attention.
 *
 * One card is expanded at a time. Opening a second closes the first: two open
 * route activities is four address searches and two sets of pit stops on one
 * screen, and nobody can tell which field belongs to which. Cards start closed
 * so the list reads as a list; a card the organizer adds opens itself, and so
 * does the first card with an error, so a blocked save lands the organizer on
 * the thing that is actually wrong.
 */

import React, { useEffect, useState } from 'react';
import ActivityCard from './ActivityCard';
import { MAX_ACTIVITIES_PER_EVENT } from '../../athlohive/validateEventForm';

export default function EventStepActivities({
  form, errors, dispatch, lookups, pendingUploads = {}, setPendingFile,
  readOnly = false,
}) {
  const activities = form.activities || [];
  const activityErrors = errors.activityErrors || [];
  const [expandedIndex, setExpandedIndex] = useState(null);

  // When a save is blocked, open the card that blocked it.
  const firstBroken = activityErrors.findIndex(
    (entry) => entry && Object.keys(entry).length > 0
  );
  useEffect(() => {
    if (firstBroken >= 0) setExpandedIndex(firstBroken);
  }, [firstBroken]);

  const atCap = activities.length >= MAX_ACTIVITIES_PER_EVENT;

  return (
    <section className="org-card">
      <p className="org-lab">
        Activities {activities.length > 0 && `· ${activities.length} of ${MAX_ACTIVITIES_PER_EVENT}`}
      </p>
      <p style={{ fontSize: 11.5, color: 'var(--org-dim)', margin: '6px 0 15px', lineHeight: 1.55 }}>
        A category people sign up for — the 10K, the 21K, the sunrise yoga session. Every event needs
        at least one activity.
      </p>

      {errors.activities && <span className="orgf-err">{errors.activities}</span>}

      {activities.map((activity, index) => (
        <ActivityCard
          key={index}
          activity={activity}
          index={index}
          errors={activityErrors[index]}
          dispatch={dispatch}
          lookups={lookups}
          expanded={expandedIndex === index}
          onToggle={() => setExpandedIndex((current) => (current === index ? null : index))}
          pendingUploads={pendingUploads}
          setPendingFile={setPendingFile}
          readOnly={readOnly}
        />
      ))}

      {/*
        Hidden rather than disabled on a locked event: there is no activity to
        add to a finished event, and a greyed "+ Add another activity" is an
        invitation to work out why it will not respond.
      */}
      {!readOnly && (
      <button
        type="button"
        className="orgf-add"
        disabled={atCap}
        onClick={() => {
          dispatch({ type: 'addActivity' });
          setExpandedIndex(activities.length);
        }}
      >
        {activities.length === 0 ? '+ Add the first activity' : '+ Add another activity'}
      </button>
      )}

      {atCap && (
        <p className="orgf-help">
          An event can have at most {MAX_ACTIVITIES_PER_EVENT} activities.
        </p>
      )}
    </section>
  );
}
